import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";

const LAST_UPDATED = "October 6, 2026";

const LegalLayout = ({ title, tag, children, testId }) => (
  <div className="min-h-screen bg-white font-body text-black" data-testid={testId}>
    <header className="sticky top-0 z-50 w-full bg-white border-b-2 border-black">
      <div className="max-w-[900px] mx-auto flex items-center justify-between px-6 py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-logo-link">
          <LogoMark size={28} />
          <span className="font-display text-xl uppercase tracking-tight">
            The Last <span className="text-[#E60000]">Bet</span> Project
          </span>
        </Link>
        <Link
          to="/"
          data-testid="legal-back-link"
          className="inline-flex items-center gap-2 border-2 border-black bg-white px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-black hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </div>
    </header>
    <main className="max-w-[900px] mx-auto px-6 py-16 lg:py-20">
      <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#E60000] mb-4">{tag}</div>
      <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.9] mb-4" data-testid={`${testId}-title`}>
        {title}
      </h1>
      <p className="text-xs uppercase tracking-widest font-bold text-neutral-500 mb-10">
        Last updated: {LAST_UPDATED}
      </p>
      <div className="space-y-8 text-base lg:text-lg font-medium text-neutral-800 leading-relaxed">
        {children}
      </div>
      <div className="mt-14 border-2 border-black bg-[#F5F2EC] p-6 text-sm">
        <span className="uppercase tracking-widest font-bold text-xs text-[#E60000]">Questions?</span>
        <p className="mt-2 font-medium text-neutral-700">
          Reply to any email you've received from us (your order confirmation includes
          our reply-to address) and a real human will get back to you.
        </p>
      </div>
    </main>
    <footer className="border-t-2 border-black">
      <div className="max-w-[900px] mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-neutral-500 uppercase tracking-widest font-bold">
        <span>© {new Date().getFullYear()} The Last Bet Project</span>
        <span className="flex gap-6">
          <Link to="/privacy" data-testid="legal-footer-privacy" className="hover:text-[#E60000]">Privacy Policy</Link>
          <Link to="/terms" data-testid="legal-footer-terms" className="hover:text-[#E60000]">Terms of Service</Link>
        </span>
      </div>
    </footer>
  </div>
);

const H = ({ children }) => (
  <h2 className="font-display text-2xl lg:text-3xl uppercase tracking-tight text-black pt-4">
    {children}
  </h2>
);

export const PrivacyPolicy = () => (
  <LegalLayout title="Privacy Policy" tag="Your privacy is sacred" testId="privacy-page">
    <p>
      The Last Bet Project ("we", "us") is a small, independent recovery project.
      Recovery is private work, and we treat your information the way we would want
      our own treated during the hardest chapter of our lives. This policy explains,
      in plain language, what we collect, why, and what we never do.
    </p>

    <H>What we collect</H>
    <p>
      <span className="font-bold text-black">When you buy the ebook or donate:</span>{" "}
      your name and email address, so we can deliver your purchase and your receipt.
      Payment details (card numbers) go directly to Stripe, our payment processor —
      we never see, store, or touch your full card number.
    </p>
    <p>
      <span className="font-bold text-black">When you join the newsletter:</span>{" "}
      just your email address, so we can send recovery tips.
    </p>
    <p>
      <span className="font-bold text-black">Automatically:</span> basic, anonymized
      usage analytics (pages visited, approximate region) that help us understand
      whether the site is helping people.
    </p>

    <H>How we use it</H>
    <p>
      To deliver your ebook instantly by email, process your donation, send your
      receipt, and — only if you subscribed — send occasional recovery tips. That's
      the whole list.
    </p>

    <H>What we never do</H>
    <p>
      We never sell, rent, or share your personal information with marketers. We
      never publish purchase or donation records. We never email you about anything
      you didn't ask for. Your relationship with this project is nobody else's
      business.
    </p>

    <H>Third-party services</H>
    <p>
      We use Stripe to process payments securely (their privacy policy applies to
      payment data), a transactional email service to deliver orders, and privacy-respecting
      analytics to understand site traffic. Each receives only the minimum data needed
      to perform its function.
    </p>

    <H>Your rights</H>
    <p>
      You can ask us at any time to show you what we hold about you, correct it, or
      delete it entirely. Reply to any email from us and we will take care of it —
      no forms, no runaround. Newsletter emails include a one-click unsubscribe.
    </p>

    <H>Data retention & security</H>
    <p>
      We keep order records as long as needed for accounting and legal purposes.
      Data is stored with reputable cloud providers and access is limited to the
      small team running this project.
    </p>

    <H>Changes</H>
    <p>
      If this policy changes, the "last updated" date above will change. Material
      changes will be announced on this page.
    </p>
  </LegalLayout>
);

export const TermsOfService = () => (
  <LegalLayout title="Terms of Service" tag="The straight talk" testId="terms-page">
    <p>
      These terms govern your purchase of the ebook and any donation you make to
      The Last Bet Project. We've written them in plain English because legal fog
      helps no one.
    </p>

    <H>Not medical advice</H>
    <p>
      The ebook and this website are a recovery field manual built from lived
      experience. They are <span className="font-bold text-black">not</span> medical,
      psychological, financial, or legal advice, and they are not a substitute for
      professional care. If you are in crisis, please contact your local helpline
      or emergency services immediately.
    </p>

    <H>The ebook</H>
    <p>
      The ebook is completely free — no payment, no card required. Claim it with
      your name and email and it's delivered instantly, right on the page and to
      your inbox. Your download link remains active for 30 days. The ebook is
      licensed for your personal use only — please don't redistribute or resell
      it; sharing it widely would directly undermine the project that keeps it
      free for people who can't afford help.
    </p>

    <H>Payments & donations</H>
    <p>
      Donations are voluntary, in US dollars, and processed securely by Stripe.
      Monthly donations renew automatically until you cancel — you can cancel
      anytime through the supporter portal link in your donation email, or by
      contacting us. Canceling stops future charges; already-processed donations
      are not refunded except in cases of clear error (contact us and we'll make
      it right).
    </p>

    <H>Donations</H>
    <p>
      Donations are voluntary and keep this project alive. One-time donations are
      generally non-refundable except in cases of clear error (contact us and we'll
      make it right). Monthly donations renew automatically until you cancel — you
      can cancel anytime through the supporter portal link in your donation email,
      or by contacting us. Canceling stops future charges; already-processed
      donations are not refunded.
    </p>

    <H>Acceptable use</H>
    <p>
      Don't misuse this site: no attempts to breach security, scrape at scale, or
      interfere with the service for others.
    </p>

    <H>Limitation of liability</H>
    <p>
      To the maximum extent permitted by law, The Last Bet Project is not liable
      for any indirect, incidental, or consequential damages arising from use of
      this site or the ebook. Our total liability is limited to the amount you
      paid us in the 12 months preceding the claim.
    </p>

    <H>Changes</H>
    <p>
      We may update these terms as the project grows. The "last updated" date
      above reflects the current version. Continued use of the site after changes
      means you accept them.
    </p>
  </LegalLayout>
);
